<template>
  <main class="customer-groups-page">
    <section class="customer-groups-page__panel">
      <h1>Kundklubbar</h1>
      <p>Uppdatera kundklubbsnivåer baserat på föregående säsongs slutdatum.</p>

      <p v-if="submitError" class="container-border container-border--error-info" role="alert">
        {{ submitError }}
      </p>

      <p v-if="submitSuccess" class="container-border container-border--success-info" role="status">
        {{ submitSuccess }}
      </p>

      <form class="customer-groups-page__form" @submit.prevent="submit">
        <Input
          v-model="seasonEndDate"
          id="season-end-date"
          type="date"
          label="Föregående säsongs slutdatum"
          :is-invalid="!!seasonEndDateError"
          :error-message="seasonEndDateError"
        />

        <button class="button button--large" type="submit" :disabled="loading">
          Uppdatera kundklubbsnivåer
        </button>

        <div v-if="loading" class="customer-groups-page__loading" aria-live="polite">
          <Spinner />
          <span>Uppdaterar...</span>
        </div>
      </form>
    </section>

    <section v-if="messages.length > 0" class="customer-groups-page__panel">
      <h2>Resultat</h2>
      <ul class="customer-groups-page__results">
        <li v-for="(message, index) in messages" :key="index">
          {{ message }}
        </li>
      </ul>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useField, useForm } from "vee-validate";
import Input from "@components/Input/Input.vue";
import Spinner from "@components/Spinner/Spinner.vue";
import { useApi } from "@shared/Services/axios-service";

type UpgradeClubMemberLevelRequest = {
  seasonEndDate: string;
};

const submitError = ref("");
const submitSuccess = ref("");
const messages = ref<string[]>([]);

const { handleSubmit } = useForm();
const { value: seasonEndDate, errorMessage: seasonEndDateError } = useField<string>(
  "seasonEndDate",
  (value?: string) => value ? true : "Du måste välja ett datum",
);

const { post, loading, errorMessage } = useApi<string[]>("/customergroups/upgradeclubmemberlevel");

const apiErrorText = computed(() => {
  const error = errorMessage.value;

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = error.message;
    return Array.isArray(message) ? message.join(", ") : String(message ?? "");
  }

  return "";
});

const submit = handleSubmit(async () => {
  submitError.value = "";
  submitSuccess.value = "";
  messages.value = [];

  try {
    const response = await post<string[], UpgradeClubMemberLevelRequest>({
      seasonEndDate: seasonEndDate.value,
    });

    messages.value = response;
    submitSuccess.value = "Klubbnivåer uppdaterade.";
  } catch {
    submitError.value = apiErrorText.value || "Kunde inte uppdatera kundklubbsnivåer";
  }
});
</script>
